import React, { useState, useEffect, useRef } from "react";
import "./wellsentials.css";
import { KinesisClient, PutRecordCommand } from "@aws-sdk/client-kinesis";
import { SNSClient, PublishCommand } from "@aws-sdk/client-sns";
import { fromCognitoIdentityPool } from "@aws-sdk/credential-providers";
import { S3Client, PutObjectCommand, GetObjectCommand } from "@aws-sdk/client-s3";
import { getSignedUrl } from "@aws-sdk/s3-request-presigner";

const AWS_REGION = "us-west-2";
const IDENTITY_POOL_ID = "us-west-2:6912f649-1ac4-4910-9622-5e61ad8fbe22";
const SNS_TOPIC_HELP = "arn:aws:sns:us-west-2:101845606311:wellsentials";
const SNS_TOPIC_CALLBACK = "arn:aws:sns:us-west-2:101845606311:wellsentialscallback";
const S3_BUCKET = "vitals.wellsentials.net";
const S3_UPLOAD_PREFIX = "vitalsuploads";

let kinesisClient = new KinesisClient({ region: AWS_REGION });
let snsClient = new SNSClient({ region: AWS_REGION });
let s3Client = new S3Client({ region: AWS_REGION });

// ---------- helpers: randoms & generators ----------
const rand = (min, max) => Math.floor(Math.random() * (max - min + 1)) + min;
const randFloat = (min, max, decimals = 1) => parseFloat((Math.random() * (max - min) + min).toFixed(decimals));

const HEALTH_PROFILES = { HEALTHY: "Healthy", NORMAL: "Normal", UNHEALTHY: "Unhealthy" };

function generateVitalsRecord(profile, patientName, patientNumber) {
  let hr, sys, dia, tempF, rr, spo2;
  switch (profile) {
    case HEALTH_PROFILES.HEALTHY:
      hr = rand(58, 72); sys = rand(105, 118); dia = rand(65, 78); tempF = randFloat(97.7, 98.6, 1); rr = rand(12, 16); spo2 = rand(97, 100);
      break;
    case HEALTH_PROFILES.UNHEALTHY:
      hr = rand(95, 120); sys = rand(140, 160); dia = rand(90, 100); tempF = randFloat(99.5, 102.0, 1); rr = rand(20, 28); spo2 = rand(88, 95);
      break;
    default:
      hr = rand(60, 90); sys = rand(115, 125); dia = rand(75, 85); tempF = randFloat(97.0, 99.0, 1); rr = rand(12, 20); spo2 = rand(95, 99);
  }
  const weightLbs = rand(120, 220);
  const heightIn = rand(60, 75);
  const name = `TEST_${patientName || "Patient"}_${patientNumber}`;
  const id = `T-${String(patientNumber).padStart(4, "0")}`;
  return {
    type: "vitals",
    dataSource: "TEST_GENERATOR",
    patientName: name,
    patientId: id,
    bloodPressure: { systolic: sys, diastolic: dia },
    heartRate: hr,
    temperature: tempF,
    respiratoryRate: rr,
    oxygenSaturation: spo2,
    weight: weightLbs,
    height: heightIn,
    notes: "Auto-generated test vitals",
    timestamp: new Date().toISOString(),
  };
}

function App() {
  // General forms
  const [helpMessage, setHelpMessage] = useState("");
  const [callbackNumber, setCallbackNumber] = useState("");

  // Patient form states
  const [patientName, setPatientName] = useState("");
  const [patientId, setPatientId] = useState("");
  const [systolic, setSystolic] = useState("");
  const [diastolic, setDiastolic] = useState("");
  const [heartRate, setHeartRate] = useState("");
  const [temperature, setTemperature] = useState("");
  const [respRate, setRespRate] = useState("");
  const [oxygenSat, setOxygenSat] = useState("");
  const [weight, setWeight] = useState("");
  const [pain, setPain] = useState(0);
  const [height, setHeight] = useState("");
  const [notes, setNotes] = useState("");

  // Login states
  const [zipCode, setZipCode] = useState("");
  const [authState, setAuthState] = useState("Welcome");
  const [isLoggedIn, setIsLoggedIn] = useState(false);

  // Testing UI state
  const [showTester, setShowTester] = useState(false);
  const [testNumPatients, setTestNumPatients] = useState(10);
  const [testBaseName, setTestBaseName] = useState("Patient");
  const [testProfile, setTestProfile] = useState(HEALTH_PROFILES.NORMAL);
  const [testDurationSec, setTestDurationSec] = useState(60);
  const [countdown, setCountdown] = useState(0);
  const [previewActive, setPreviewActive] = useState(false);
  const [testRunning, setTestRunning] = useState(false);
  const [testId, setTestId] = useState(null);

  // ✅ Upload UI state
  const [uploadedUrls, setUploadedUrls] = useState([]);
  const [uploadedUrlsText, setUploadedUrlsText] = useState("");
  const [uploadProgress, setUploadProgress] = useState({}); // key -> percent
  const [isUploading, setIsUploading] = useState(false);

  // refs for intervals
  const previewIntervalRef = useRef(null);
  const countdownIntervalRef = useRef(null);
  const testIntervalRef = useRef(null);
  const testEndAtRef = useRef(0);

  // hidden file input ref
  const fileInputRef = useRef(null);

  // 🔹 Fire GA page_view when app loads
  useEffect(() => {
    if (window.gtag) {
      const pagePath = window.location.pathname + window.location.search;
      window.gtag("event", "page_view", { event_category: "Navigation", event_label: `App Loaded: ${pagePath}`, page_path: pagePath });
    }
  }, []);

  // cleanup on unmount
  useEffect(() => {
    return () => {
      if (previewIntervalRef.current) clearInterval(previewIntervalRef.current);
      if (countdownIntervalRef.current) clearInterval(countdownIntervalRef.current);
      if (testIntervalRef.current) clearInterval(testIntervalRef.current);
    };
  }, []);

  // warn before leaving during a run or countdown
  useEffect(() => {
    const handler = (e) => { if (testRunning || countdown > 0) { e.preventDefault(); e.returnValue = ""; } };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [testRunning, countdown]);

  // === Cognito Login ===
  const handleLogin = async () => {
    try {
      if (zipCode !== "92101") {
        console.error("❌ WRONG Zip Code");
        setAuthState("❌ Zip Code");
        setIsLoggedIn(false);
        if (window.gtag) window.gtag("event", "login_failure", { event_category: "Authentication", event_label: `Invalid Zip Code: ${zipCode}` });
        return;
      }
      console.log("Zip Code entered:", zipCode);
      const credentialsProvider = fromCognitoIdentityPool({ clientConfig: { region: AWS_REGION }, identityPoolId: IDENTITY_POOL_ID });

      kinesisClient = new KinesisClient({ region: AWS_REGION, credentials: credentialsProvider });
      snsClient = new SNSClient({ region: AWS_REGION, credentials: credentialsProvider });
      s3Client = new S3Client({ region: AWS_REGION, credentials: credentialsProvider });

      const resolvedCreds = await credentialsProvider();
      console.log("✅ Cognito credentials resolved:", resolvedCreds);

      setAuthState(`Logged In:  Anonymous`);
      setIsLoggedIn(true);
      if (window.gtag) window.gtag("event", "login_success", { event_category: "Authentication", event_label: `IdentityId: ${resolvedCreds.identityId || "Anonymous"}` });
    } catch (error) {
      console.error("❌ Error logging in with Cognito:", error);
      setAuthState("Failed");
      setIsLoggedIn(false);
      if (window.gtag) window.gtag("event", "login_error", { event_category: "Authentication", event_label: error.message || "Unknown Error" });
    }
  };

  // === Kinesis + SNS helpers ===
  const sendToKinesis = async (data, partitionKey = "default") => {
    try {
      const command = new PutRecordCommand({ StreamName: "wellsentials", Data: new TextEncoder().encode(JSON.stringify(data)), PartitionKey: partitionKey });
      await kinesisClient.send(command);
      console.log("✅ Data sent to Kinesis:", data);
    } catch (error) {
      console.error("❌ Error sending to Kinesis:", error);
    }
  };

  const sendToSNS = async (topicArn, message, subject) => {
    try {
      const command = new PublishCommand({ TopicArn: topicArn, Message: message, Subject: subject });
      await snsClient.send(command);
      console.log(`✅ Message published to ${topicArn}:`, message);
    } catch (error) {
      console.error("❌ Error publishing to SNS:", error);
    }
  };

  // === Form handlers ===
  const handleHelp = (e) => {
    e.preventDefault();
    if (!isLoggedIn) { alert("⚠️ Please log in first by entering your Zip Code at the top."); return; }
    sendToKinesis({ type: "help", message: helpMessage }, "help");
    sendToSNS(SNS_TOPIC_HELP, helpMessage, "Help Patient");
    if (window.gtag) window.gtag("event", "request_help", { event_category: "Patient Actions", event_label: "Help Message Sent" });
    setHelpMessage("");
  };

  const handleRequestCallback = (e) => {
    e.preventDefault();
    if (!isLoggedIn) { alert("⚠️ Please log in first by entering your Zip Code at the top."); return; }
    sendToKinesis({ type: "callback", phone: callbackNumber }, "callback");
    sendToSNS(SNS_TOPIC_CALLBACK, callbackNumber, "Request Callback");
    if (window.gtag) window.gtag("event", "request_callback", { event_category: "Patient Actions", event_label: "Callback Requested" });
    setCallbackNumber("");
  };

  const handleSendVitals = (e) => {
    e.preventDefault();
    if (!isLoggedIn) { alert("⚠️ Please log in first by entering your Zip Code at the top."); return; }
    const vitalsData = {
      type: "vitals",
      patientName, patientId,
      bloodPressure: { systolic, diastolic },
      heartRate, temperature,
      respiratoryRate: respRate, oxygenSaturation: oxygenSat,
      weight, height, notes,
      timestamp: new Date().toISOString(),
    };
    sendToKinesis(vitalsData, "vitals");
    if (window.gtag) window.gtag("event", "record_vitals", { event_category: "Patient Actions", event_label: `Vitals recorded for ${patientName || "Unknown"}` });
  };

  const clearVitals = () => {
    setPatientName(""); setPatientId(""); setSystolic(""); setDiastolic(""); setHeartRate("");
    setTemperature(""); setRespRate(""); setOxygenSat(""); setWeight(""); setHeight(""); setNotes("");
  };

  // ---------- TESTING MODULE LOGIC ----------
  const startPreview = () => {
    if (!isLoggedIn) { alert("⚠️ Please log in first by entering your Zip Code at the top."); return; }
    if (testRunning) return;
    if (previewIntervalRef.current) clearInterval(previewIntervalRef.current);
    setPreviewActive(true);
    previewIntervalRef.current = setInterval(() => {
      const rec = generateVitalsRecord(testProfile, testBaseName, 1);
      setPatientName(rec.patientName); setPatientId(rec.patientId);
      setSystolic(rec.bloodPressure.systolic); setDiastolic(rec.bloodPressure.diastolic);
      setHeartRate(rec.heartRate); setTemperature(rec.temperature);
      setRespRate(rec.respiratoryRate); setOxygenSat(rec.oxygenSaturation);
      setWeight(rec.weight); setHeight(rec.height); setNotes(rec.notes);
    }, 1000);
  };

  const stopPreview = () => {
    setPreviewActive(false);
    if (previewIntervalRef.current) { clearInterval(previewIntervalRef.current); previewIntervalRef.current = null; }
  };

  const handleGenerateAndFill = () => { if (previewActive) stopPreview(); else startPreview(); };

  const stopTest = () => {
    if (countdownIntervalRef.current) clearInterval(countdownIntervalRef.current);
    if (testIntervalRef.current) clearInterval(testIntervalRef.current);
    countdownIntervalRef.current = null; testIntervalRef.current = null;
    setCountdown(0); setTestRunning(false);
    if (window.gtag && testId) window.gtag("event", "test_abort", { event_category: "Testing", test_id: testId });
  };

  const startAutoGenerate = async () => {
    if (!isLoggedIn) { alert("⚠️ Please log in first by entering your Zip Code at the top."); return; }
    if (testRunning) return;
    if (previewActive) stopPreview();
    setCountdown(5);
    const paramsSummary = `patients=${testNumPatients}, baseName="${testBaseName}", profile=${testProfile}, duration=${testDurationSec}s`;
    await sendToSNS(SNS_TOPIC_HELP, `generating data - ${paramsSummary}`, "testing vitals");
    if (countdownIntervalRef.current) clearInterval(countdownIntervalRef.current);
    countdownIntervalRef.current = setInterval(() => {
      setCountdown((c) => { if (c <= 1) { clearInterval(countdownIntervalRef.current); countdownIntervalRef.current = null; beginTimedSends(); } return c - 1; });
    }, 1000);
  };

  const beginTimedSends = () => {
    setTestRunning(true);
    const id = `test_${Date.now()}`; setTestId(id);
    if (window.gtag) window.gtag("event", "test_start", { event_category: "Testing", event_label: `patients=${testNumPatients}, profile=${testProfile}, dur=${testDurationSec}s`, test_id: id });
    const endAt = Date.now() + testDurationSec * 1000; testEndAtRef.current = endAt;
    if (testIntervalRef.current) clearInterval(testIntervalRef.current);
    testIntervalRef.current = setInterval(async () => {
      const now = Date.now();
      if (now >= testEndAtRef.current) {
        clearInterval(testIntervalRef.current); testIntervalRef.current = null;
        setTestRunning(false); setCountdown(0);
        if (window.gtag && id) window.gtag("event", "test_stop", { event_category: "Testing", test_id: id });
        return;
      }
      const promises = [];
      for (let i = 1; i <= Number(testNumPatients || 0); i++) {
        const rec = generateVitalsRecord(testProfile, testBaseName, i);
        if (i === 1) {
          setPatientName(rec.patientName); setPatientId(rec.patientId);
          setSystolic(rec.bloodPressure.systolic); setDiastolic(rec.bloodPressure.diastolic);
          setHeartRate(rec.heartRate); setTemperature(rec.temperature);
          setRespRate(rec.respiratoryRate); setOxygenSat(rec.oxygenSaturation);
          setWeight(rec.weight); setHeight(rec.height); setNotes(rec.notes);
        }
        promises.push(sendToKinesis(rec, "default"));
      }
      try { await Promise.all(promises); } catch (e) { console.error("❌ Error sending batch:", e); }
    }, 1000);
  };

  // ---------- FILE UPLOADS (Presigned PUT + Progress) ----------
  const handleUploadClick = () => {
    if (!isLoggedIn) { alert("⚠️ Please log in first by entering your Zip Code at the top."); return; }
    fileInputRef.current?.click();
  };

  const uploadFileWithProgress = async (url, file, onProgress) => {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open("PUT", url, true);
      xhr.setRequestHeader("Content-Type", file.type || "application/octet-stream");
      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable) onProgress(Math.round((e.loaded / e.total) * 100));
      };
      xhr.onload = () => (xhr.status >= 200 && xhr.status < 300) ? resolve() : reject(new Error(`Upload failed (${xhr.status})`));
      xhr.onerror = () => reject(new Error("Network error during upload"));
      xhr.send(file);
    });
  };

  const handleFilesSelected = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;

    setIsUploading(true);
    setUploadProgress({});
    try {
      const urls = [];
      const uploadedLines = [];

      for (const file of files) {
        const key = `${S3_UPLOAD_PREFIX}/${Date.now()}-${Math.random().toString(36).slice(2, 8)}-${file.name.replace(/\s+/g, "_")}`;

        // 1) Presigned PUT URL for upload
        const putUrl = await getSignedUrl(
          s3Client,
          new PutObjectCommand({ Bucket: S3_BUCKET, Key: key, ContentType: file.type || "application/octet-stream" }),
          { expiresIn: 3600 }
        );

        // 2) Upload with progress
        setUploadProgress((p) => ({ ...p, [key]: 0 }));
        await uploadFileWithProgress(putUrl, file, (pct) => setUploadProgress((p) => ({ ...p, [key]: pct })));

        // 3) Presigned GET to show the file
        const getUrl = await getSignedUrl(
          s3Client,
          new GetObjectCommand({ Bucket: S3_BUCKET, Key: key }),
          { expiresIn: 3600 }
        );

        urls.push(getUrl);
        uploadedLines.push(`• ${file.name} → s3://${S3_BUCKET}/${key}\n${getUrl}`);
        console.log("✅ Uploaded:", key);
      }

      setUploadedUrls(urls);
      setUploadedUrlsText(urls.join("\n"));
      e.target.value = "";

      // SNS notification (success summary)
      const body = `Upload time: ${new Date().toLocaleString()}

Files:
${uploadedLines.join("\n\n")}

(Links valid for 1 hour)
`;
      await sendToSNS(SNS_TOPIC_HELP, body, "files uploaded by vitals App");
      alert("✅ Upload completed!");
    } catch (err) {
      console.error("❌ Upload failed:", err);
      alert("❌ Upload failed. Check console for details.");
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div className="app-container">
      <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: "1rem" }}>
        <div style={{ textAlign: "right" }}>
          <input
            type="text"
            placeholder="Zip Code"
            value={zipCode}
            onChange={(e) => setZipCode(e.target.value)}
            style={{ marginRight: "0.5rem", padding: "0.5rem" }}
          />
          <button onClick={handleLogin}>Login</button>
          <div style={{ marginTop: "0.5rem", fontWeight: "bold" }}>{authState}</div>
        </div>
      </div>

      <h1 className="text-center">Wellsentials Vitals</h1>

      {/* Help Page */}
      <div className="section">
        <h2>Help</h2>
        <form onSubmit={handleHelp}>
          <textarea
            placeholder="Describe the issue you need help with..."
            value={helpMessage}
            onChange={(e) => setHelpMessage(e.target.value)}
            required
          />
          <button type="submit" disabled={!isLoggedIn}>Request Help</button>
        </form>
      </div>

      {/* Request Callback */}
      <div className="section">
        <h2>Request Callback</h2>
        <form onSubmit={handleRequestCallback}>
          <input
            type="tel"
            placeholder="Enter your phone number"
            value={callbackNumber}
            onChange={(e) => setCallbackNumber(e.target.value)}
            required
          />
          <button type="submit" disabled={!isLoggedIn}>Request Callback</button>
        </form>
      </div>

      {/* Patient Vital Signs Form */}
      <div className="section">
        <h2 className="text-center">Patient Vital Signs</h2>
        <form onSubmit={handleSendVitals}>
          <div className="form-row">
            <label>
              Patient Name *
              <input type="text" placeholder="Enter patient's full name" value={patientName} onChange={(e) => setPatientName(e.target.value)} required />
            </label>
            <label>
              Patient ID *
              <input type="text" placeholder="Enter patient ID" value={patientId} onChange={(e) => setPatientId(e.target.value)} required />
            </label>
          </div>

          <hr />

          <h3>Vital Signs</h3>
          <div className="form-row">
            <label>
              Blood Pressure (mmHg) 
              <div className="bp-inputs">
                <input type="number" placeholder="Systolic" value={systolic} onChange={(e) => setSystolic(e.target.value)}  /> /
                <input type="number" placeholder="Diastolic" value={diastolic} onChange={(e) => setDiastolic(e.target.value)}  />
              </div>
            </label>
            <label>
              Heart Rate (bpm) *
              <input type="number" placeholder="Enter heart rate" value={heartRate} onChange={(e) => setHeartRate(e.target.value)} required />
            </label>
            <label>
              Temperature (°F) 
              <input type="number" placeholder="Enter temperature" value={temperature} onChange={(e) => setTemperature(e.target.value)}  />
            </label>
          </div>

          <div className="form-row">
            <label>
              Respiratory Rate (breaths/min) 
              <input type="number" placeholder="Enter respiratory rate" value={respRate} onChange={(e) => setRespRate(e.target.value)}  />
            </label>
            <label>
              Oxygen Saturation (%) 
              <input type="number" placeholder="Enter oxygen saturation" value={oxygenSat} onChange={(e) => setOxygenSat(e.target.value)}  />
            </label>
          </div>

          <hr />

          <h3>Physical Measurements</h3>
          <div className="form-row">
            <label>
              Weight (lbs) *
              <input type="number" placeholder="Enter weight in lbs" value={weight} onChange={(e) => setWeight(e.target.value)} required />
            </label>
            <label>
              Height (Inches) *
              <input type="number" placeholder="Enter height in Inches" value={height} onChange={(e) => setHeight(e.target.value)} required />
            </label>
          </div>

          <h3>Pain Awareness</h3>
          <div className="form-row">
            <label>
              Pain Intensity (1–10) *
              <input
                type="range"
                min="0"
                max="10"
                step="1"
                value={pain}
                onChange={(e) => setPain(e.target.value)}
                required
                style={{ width: "100%", marginTop: "0.5rem" }}
              />
              <span style={{ marginLeft: "0.5rem", fontWeight: "bold" }}>{pain}</span>
            </label>
          </div>

          <div className="form-row">
            <label>
              Clinical Notes
              <textarea placeholder="Enter any additional observations or notes..." value={notes} onChange={(e) => setNotes(e.target.value)} />
            </label>
          </div>

          <div style={{ display: "flex", justifyContent: "center", gap: "0.5rem", marginTop: "1rem" }}>
            <button type="submit" disabled={!isLoggedIn}>Record Vitals</button>
            <button type="button" onClick={clearVitals} disabled={!isLoggedIn}>CLEAR Vitals</button>
          </div>
        </form>
      </div>

      {/* ---------- FILE UPLOADS ---------- */}
      <div className="section" style={{ marginTop: "2rem" }}>
        <h2>Uploads</h2>
        <button type="button" onClick={handleUploadClick} disabled={!isLoggedIn}>Upload Files</button>
        <input ref={fileInputRef} type="file" multiple style={{ display: "none" }} onChange={handleFilesSelected} />

        {/* Progress bars per file (show during upload) */}
        {isUploading && Object.keys(uploadProgress).length > 0 && (
          <div style={{ marginTop: "1rem" }}>
            {Object.entries(uploadProgress).map(([key, pct]) => (
              <div key={key} style={{ marginBottom: 8 }}>
                <div style={{ fontSize: 12, marginBottom: 4, wordBreak: "break-all" }}>{key.replace(`${S3_UPLOAD_PREFIX}/`, "")}</div>
                <div style={{ height: 10, background: "#eee", borderRadius: 6, overflow: "hidden" }}>
                  <div style={{ width: `${pct}%`, height: "100%", background: "#4caf50", transition: "width .2s linear" }} />
                </div>
                <div style={{ fontSize: 12, marginTop: 2 }}>{pct}%</div>
              </div>
            ))}
          </div>
        )}

        {/* show URLs after upload */}
        {uploadedUrls.length > 0 && (
          <div style={{ marginTop: "1rem" }}>
            <div style={{ marginBottom: "0.5rem", fontWeight: 600 }}>Pre-signed URLs (valid 1 hour):</div>
            <textarea readOnly value={uploadedUrlsText} rows={Math.min(12, Math.max(4, uploadedUrls.length))} style={{ width: "100%", fontFamily: "monospace" }} />
            <ul style={{ marginTop: "0.5rem" }}>
              {uploadedUrls.map((u, i) => (
                <li key={i}><a href={u} target="_blank" rel="noreferrer">File {i + 1}</a></li>
              ))}
            </ul>
          </div>
        )}
      </div>

      {/* --------- VITALS TESTING CONTROLS (collapsible) --------- */}
      <div className="section" style={{ marginTop: "2rem" }}>
        <button
          type="button"
          onClick={() => setShowTester((s) => !s)}
          style={{ marginBottom: "1rem" }}
          disabled={testRunning}
          title={testRunning ? "Stop the current test to toggle" : undefined}
        >
          {showTester ? "Hide Vitals Testing Controls" : "Show Vitals Testing Controls"}
        </button>

        {showTester && (
          <div style={{ border: "1px solid #ddd", borderRadius: 8, padding: "1rem", background: "#fafafa" }}>
            <div className="form-row">
              <label>
                Number of Patients
                <input type="number" min={1} value={testNumPatients} onChange={(e) => setTestNumPatients(parseInt(e.target.value || "1", 10))} disabled={testRunning} />
              </label>
              <label>
                Base Patient Name
                <input type="text" value={testBaseName} onChange={(e) => setTestBaseName(e.target.value)} placeholder="e.g., TestPatient" disabled={testRunning} />
              </label>
              <label>
                Health Profile
                <select value={testProfile} onChange={(e) => setTestProfile(e.target.value)} disabled={testRunning}>
                  <option value={HEALTH_PROFILES.HEALTHY}>Happy and Healthy</option>
                  <option value={HEALTH_PROFILES.NORMAL}>Normal</option>
                  <option value={HEALTH_PROFILES.UNHEALTHY}>Unhappy and Unhealthy</option>
                </select>
              </label>
              <label>
                Test Duration (seconds)
                <input type="number" min={1} value={testDurationSec} onChange={(e) => setTestDurationSec(parseInt(e.target.value || "60", 10))} disabled={testRunning} />
              </label>
            </div>

            <div style={{ display: "flex", gap: "0.5rem", marginTop: "1rem", alignItems: "center", flexWrap: "wrap" }}>
              <button type="button" onClick={handleGenerateAndFill} disabled={!isLoggedIn || testRunning}>
                {previewActive ? "Stop Preview" : "Generate and Fill"}
              </button>
              <button type="button" onClick={startAutoGenerate} disabled={!isLoggedIn || testRunning}>Auto Generate - Fill and Send</button>
              <button type="button" onClick={stopTest} disabled={!testRunning}>Stop Test</button>
              <span style={{ marginLeft: "0.5rem", fontWeight: "bold" }}>
                {countdown > 0 ? `Starting in ${countdown}s…` : testRunning ? "Running…" : ""}
              </span>
            </div>
          </div>
        )}

      </div>

      {/* HIPAA badge */}
      <div className="hipaa-section" aria-label="HIPAA compliance badge" style={{ marginTop: "2rem" }}>
        <img
          src={`${process.env.PUBLIC_URL}/hippalogo.png`}  // uses /public asset
          alt="HIPAA compliance logo"
          style={{ display: "block", margin: "0 auto", maxWidth: 180, width: "40%", height: "auto", opacity: 0.95 }}
        />
      </div>




      {/* Disabled button styling */}
      <style>
        {`
          button:disabled { background-color: #ccc !important; color: #666 !important; cursor: not-allowed !important; }
        `}
      </style>


    </div>
  );
}

export default App;
